import { Formik } from "formik";
import * as yup from "yup";
import { useEffect, useRef, useState } from "react";
import { Button } from "../../../components/ui/button";
import { AppCheckBox, AppSelect } from "../../../components/appComponents";
import Card from "../../../components/ui/card-snippet";
import { useNavigate } from "react-router-dom";
import {
  FormikInput,
  FormikTextArea,
  FormikSelect,
  FormikSearchSelect,
  FormikCheckbox,
} from "../../../components/formik-components";
import Common from "../../../helper/common";
import {
  HolidayTypeEnum,
  ResultStatus,
  ToCSharpFormat,
  DayOfWeekEnum,
} from "../../../constants/appConstant";
import { useQueryParams } from "../../../contexts/queryParamsContext";
import { Icon } from "@iconify/react";

const formInitialValue = {
  startDate: "",
  endDate: "",
  reason: "",
  daysOfWeek: [],
  sportIds: [], // Changed from sportId to sportIds array
  courtIds: [],
  recurringTimes: [
    {
      start_time: "",
      end_time: "",
      description: "",
    }
  ],
};

// Dynamic validation schema based on edit mode
const getFormValidations = (isEditMode = false) => yup.object({
  startDate: yup
    .date()
    .when([], {
      is: () => !isEditMode, // Only apply past date validation for new records
      then: (schema) => schema.min(new Date(), "Start date cannot be in the past"),
      otherwise: (schema) => schema, // Skip past date validation for edit mode
    })
    .required("Start-date Time is required")
    .test(
      "is-valid-time",
      "Time of the start date format should be in 00, 30 or 23:59 format",
      (value) => Common.Utility.checkValidTimeOfDate(value)
    )
    .when([], {
      is: () => !isEditMode, // Only apply future time validation for new records
      then: (schema) => schema.test(
        "is-future-time",
        "Time of the start date cannot be in the past",
        Common.Utility.checkPreviousTimeOfDate
      ),
      otherwise: (schema) => schema, // Skip future time validation for edit mode
    }),
  endDate: yup
    .date()
    .min(yup.ref("startDate"), "End date cannot be before the start date")
    .required("End-date Time is required")
    .test(
      "is-valid-time",
      "Time of the end date format should be in 00, 30 or 23:59 format",
      (value) => Common.Utility.checkValidTimeOfDate(value)
    )
    .test(
      "is-future-time",
      "Time of the end date cannot be in the past",
      Common.Utility.checkPreviousTimeOfDate
    ),
  reason: yup.string().trim().min(10).max(200).required("Reason is Required"),
  daysOfWeek: yup.array().min(1, "Please select at least one day of the week"),
  //courtIds: yup.array().min(1, "Please select at least one court"),
  recurringTimes: yup.array().of(
    yup.object({
      start_time: yup.string().required("Start time is required"),
      end_time: yup.string().required("End time is required"),
      description: yup.string().max(200, "Description must be less than 200 characters"),
    })
  ).min(1, "At least one recurring time is required"),
});

export default function AddClubHolidayRecurring() {
  const formikRef = useRef();
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [courtList, setCourtList] = useState([]);
  const [sports, setSports] = useState([]);
  const [selectedSports, setSelectedSports] = useState([]); // Changed from selectedSport to selectedSports array
  const [isLoading, setIsLoading] = useState(false);
  const [isSpecificCourt, setIsSpecificCourt] = useState(false);
  const [availableCourts, setAvailableCourts] = useState([]); // New state for combined courts from selected sports

  const { clubHolidayRecurringId } = useQueryParams().params;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      // Load sports and courts first
      await getSports();
      const courtData = await getCourts();
      
      // Then load edit data if we have an ID
      if (clubHolidayRecurringId) {
        // Pass the court data directly to avoid timing issues
        GetHolidayRecurringDetails(clubHolidayRecurringId, courtData);
      }
    };
    fetchData();
  }, [clubHolidayRecurringId]);

  const getSports = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetClubSports"
      );
      const sportsData = [
        { id: null, name: "Select Sport", value: null, label: "Select Sport" },
        ...(response?.data ?? []).map((sport) => ({
          id: sport.id,
          name: sport.name,
          value: sport.id,
          label: sport.name,
        })),
      ];
      setSports(sportsData);
    } catch (error) {
      console.error("Error fetching sports:", error);
      Common.SweetAlert.alert("Failed to load sports", "error");
    }
  };

  const getCourts = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetSportWiseCourtddl"
      );
      if (response?.status == ResultStatus.Success) {
        // Process the data to ensure proper format for FormikSearchSelect
        const processedData = (response?.data ?? []).map(sport => ({
          ...sport,
          label: sport.label || sport.Label, // Handle both cases for sport label too
          options: sport.options?.map(court => ({
            value: court.value,
            label: court.label || court.Label || court.name, // Handle multiple possible label properties
            displayText: `${court.label || court.Label || court.name} (${sport.label || sport.Label})`, // User-friendly display format
            sportId: court.sportId,
            sportName: sport.label || sport.Label, // Store sport name for reference
            formats: court.formats
          })) || []
        }));
        console.log("Processed court data:", processedData);
        setCourtList(processedData);
        return processedData; // Return the data for use in edit
      }
    } catch (error) {
      console.error("Error fetching courts:", error);
      Common.SweetAlert.alert("Failed to load courts", "error");
    }
  };

  // Function to get combined courts from selected sports without duplication
  const getCombinedCourts = (selectedSportIds) => {
    console.log("getCombinedCourts called with:", selectedSportIds);
    console.log("Current courtList:", courtList);
    
    if (!selectedSportIds || selectedSportIds.length === 0) {
      setAvailableCourts([]);
      return;
    }

    const combinedCourts = [];
    const courtMap = new Map(); // To avoid duplicates

    selectedSportIds.forEach(sportId => {
      console.log("Looking for sportId:", sportId);
      const sportData = courtList.find(sport => sport.value === sportId);
      console.log("Found sport data:", sportData);
      
      if (sportData && sportData.options) {
        sportData.options.forEach(court => {
          // Use court.value as key to avoid duplicates
          if (!courtMap.has(court.value)) {
            courtMap.set(court.value, {
              ...court,
              sportName: sportData.label, // Add sport name for reference
              displayText: `${court.label || court.Label || court.name} (${sportData.label})`, // User-friendly display format
            });
          }
        });
      }
    });

    const finalCourts = Array.from(courtMap.values());
    console.log("Final combined courts:", finalCourts);
    setAvailableCourts(finalCourts);
  };

  // Function to get combined courts with specific court data (for edit mode)
  const getCombinedCourtsWithData = (selectedSportIds, courtData) => {
    console.log("getCombinedCourtsWithData called with:", selectedSportIds, courtData);
    
    if (!selectedSportIds || selectedSportIds.length === 0) {
      setAvailableCourts([]);
      return;
    }

    const courtMap = new Map(); // To avoid duplicates

    selectedSportIds.forEach(sportId => {
      console.log("Looking for sportId:", sportId);
      const sportData = courtData.find(sport => sport.value === sportId);
      console.log("Found sport data:", sportData);
      
      if (sportData && sportData.options) {
        sportData.options.forEach(court => {
          // Use court.value as key to avoid duplicates
          if (!courtMap.has(court.value)) {
            courtMap.set(court.value, {
              ...court,
              sportName: sportData.label, // Add sport name for reference
              displayText: `${court.label || court.Label || court.name} (${sportData.label})`, // User-friendly display format
            });
          }
        });
      }
    });

    const finalCourts = Array.from(courtMap.values());
    console.log("Final combined courts with data:", finalCourts);
    setAvailableCourts(finalCourts);
  };

  const GetHolidayRecurringDetails = async (id, courtData = null) => {
    try {
      if (!id || id === 0) {
        return;
      }
      
      setIsLoading(true);
      let response = await Common.ApiService.getInstance().request(
        `GetClubHolidayRecurring`,
        { 
          pageNumber: 1,
          search_term: "",
          recordsToDisplay: 1000 // Get more records to find the specific one
        },
        "POST"
      );
      // Handle the new API response structure
      let d = null;
      if (response?.status == ResultStatus.Success) {
        if (response?.data?.[0]?.data) {
          // New API structure - find the record with matching ID
          const records = response.data[0].data;
          d = records.find(record => record.id === parseInt(id));
        } else if (response?.data?.[0]) {
          // Fallback for old structure
          d = response.data[0];
        } else if (response?.data) {
          // Direct data structure
          d = response.data;
        }
      }
      
      if (d) {
        console.log("Edit data received:", d);
        console.log("CourtIds from edit data:", d.courtIds);
        console.log("SportId from edit data:", d.sportId);
        
        if (formikRef.current) {
          // Extract sport IDs from court data
          let sportIds = [];
          if (d.courtIds && d.courtIds.length > 0) {
            console.log("Processing courtIds:", d.courtIds);
            console.log("First court structure:", d.courtIds[0]);
            
            // Get unique sport IDs from court data
            const uniqueSportIds = [...new Set(d.courtIds.map(court => {
              console.log("Court object:", court);
              console.log("Court sportId:", court.sportId);
              return court.sportId;
            }).filter(Boolean))];
            console.log("Extracted sport IDs from courts:", uniqueSportIds);
            
            if (uniqueSportIds.length > 0) {
              sportIds = uniqueSportIds;
            } else {
              // Fallback: try to find sports by matching court IDs with court list
              console.log("No sportId in court data, trying to match with court list");
              const courtsToUse = courtData || courtList;
              if (courtsToUse && courtsToUse.length > 0) {
                const foundSports = [];
                d.courtIds.forEach(courtId => {
                  courtsToUse.forEach(sport => {
                    if (sport.options) {
                      const foundCourt = sport.options.find(court => court.value === courtId);
                      if (foundCourt && !foundSports.includes(sport.value)) {
                        foundSports.push(sport.value);
                      }
                    }
                  });
                });
                console.log("Found sports by matching court IDs:", foundSports);
                sportIds = foundSports;
              }
            }
          } else if (d.sportId) {
            // Fallback to single sportId if courtIds don't have sportId
            console.log("Using fallback sportId:", d.sportId);
            sportIds = [d.sportId];
          }

          const formData = {
            startDate: d.start_date ? Common.moment(d.start_date).format("YYYY-MM-DDTHH:mm") : "",
            endDate: d.end_date ? Common.moment(d.end_date).format("YYYY-MM-DDTHH:mm") : "",
            reason: d.reason || "",
            daysOfWeek: d.daysofweek ? d.daysofweek.split(',').map(day => day.trim()) : [],
            sportIds: sportIds, // Changed from sportId to sportIds array
            courtIds: d.courtIds || [],
            recurringTimes: d.recurringTimes && d.recurringTimes.length > 0 
              ? d.recurringTimes.map(rt => {                  
                  // Handle different time formats
                  let startTime = "";
                  let endTime = "";
                  
                  if (rt.start_time) {
                    if (typeof rt.start_time === 'string') {
                      startTime = rt.start_time.substring(0, 5);
                    } else if (rt.start_time.hours !== undefined) {
                      // TimeSpan object with hours, minutes, seconds
                      startTime = `${rt.start_time.hours.toString().padStart(2, '0')}:${rt.start_time.minutes.toString().padStart(2, '0')}`;
                    } else {
                      startTime = rt.start_time.toString().substring(0, 5);
                    }
                  }
                  
                  if (rt.end_time) {
                    if (typeof rt.end_time === 'string') {
                      endTime = rt.end_time.substring(0, 5);
                    } else if (rt.end_time.hours !== undefined) {
                      // TimeSpan object with hours, minutes, seconds
                      endTime = `${rt.end_time.hours.toString().padStart(2, '0')}:${rt.end_time.minutes.toString().padStart(2, '0')}`;
                    } else {
                      endTime = rt.end_time.toString().substring(0, 5);
                    }
                  }
                  
                  return {
                    start_time: startTime,
                    end_time: endTime,
                    description: rt.description || "",
                  };
                })
              : [{ start_time: "", end_time: "", description: "" }],
          };
          
          // Set the isSpecificCourt state based on whether courts are selected
          const hasCourts = d.courtIds && d.courtIds.length > 0;
          setIsSpecificCourt(hasCourts);
          
          // Set selected sports and update available courts
          console.log("Final sportIds to set:", sportIds);
          console.log("Court list available:", courtList);
          console.log("Passed court data:", courtData);
          
          if (sportIds.length > 0) {
            setSelectedSports(sportIds);
            // Update available courts based on selected sports
            // Use passed courtData if available, otherwise use state
            const courtsToUse = courtData || courtList;
            if (courtsToUse && courtsToUse.length > 0) {
              getCombinedCourtsWithData(sportIds, courtsToUse);
            } else {
              setTimeout(() => {
                getCombinedCourts(sportIds);
              }, 100);
            }
          }
          
          // Use setTimeout to ensure form is fully initialized
          setTimeout(() => {
            if (formikRef.current) {
              formikRef.current.setValues(formData);
            }
          }, 100);
        } else {
          // Retry after a short delay
          setTimeout(() => {
            GetHolidayRecurringDetails(id);
          }, 200);
        }
      } else {
        Common.SweetAlert.alert("No data found for this holiday", "error");
      }
      
      if (!d) {
        Common.SweetAlert.alert("Holiday record not found", "error");
      }
    } catch (error) {
      Common.SweetAlert.alert("Failed to load recurring holiday details", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitData = async (d, action) => {
    try {
      // Validate sport selection when specific court is enabled
      if (isSpecificCourt && (!d.sportIds || d.sportIds.length === 0)) {
        Common.showToast("Please select at least one sport when specific court is enabled");
        action.setSubmitting(false);
        return;
      }

      // Map frontend field names to backend field names according to EnClubHolidayRecurring DTO
      const courtIdsToSend = isSpecificCourt ? (d.courtIds || []) : [];
      
      let data = {
        id: clubHolidayRecurringId || 0,
        club_id: 0, // Will be set by backend from login.defaultClubId
        start_date: Common.moment(`${d.startDate}`).format(ToCSharpFormat),
        end_date: Common.moment(`${d.endDate}`).format(ToCSharpFormat),
        reason: d.reason,
        daysofweek: d.daysOfWeek && d.daysOfWeek.length > 0 ? d.daysOfWeek.join(',') : null,
        is_deleted: false,
        courtIds: courtIdsToSend,
        RecurringTimes: (d.recurringTimes || []).map(rt => ({
          id: 0, // Will be set by backend for new records
          clubHolidayRecurringId: 0, // Will be set by backend
          start_time: rt.start_time,
          end_time: rt.end_time,
          description: rt.description || "",
          is_deleted: false
        })),
      };

      if (data.start_date >= data.end_date) {
        Common.showToast("EndTime must be greater");
        action.setSubmitting(false);
        return;
      }

      // Check if at least one recurring time is provided
      if (!d.recurringTimes || d.recurringTimes.length === 0) {
        Common.showToast("Please add at least one recurring time slot");
        action.setSubmitting(false);
        return;
      }

      // Validate recurring times
      if (data.RecurringTimes && data.RecurringTimes.length > 0) {
        for (let i = 0; i < data.RecurringTimes.length; i++) {
          const rt = data.RecurringTimes[i];
          if (!rt.start_time || !rt.end_time) {
            Common.showToast(`Please fill in start and end times for time slot ${i + 1}`);
            action.setSubmitting(false);
            return;
          }
          if (rt.start_time >= rt.end_time) {
            Common.showToast(`End time must be greater than start time for time slot ${i + 1}`);
            action.setSubmitting(false);
            return;
          }
        }
      }
      console.log(data, 'data send')
      let response = await Common.ApiService.getInstance().request(
        "AddUpdateClubHolidayRecurring",
        data,
        "POST"
      );
      
      action.setSubmitting(false);
      
      if (response?.status == ResultStatus.Success) {
        Common.SweetAlert.alert(response.message, "success");
        setTimeout(() => {
          navigate("/app/clubholidayrecurrings");
        }, 1000);
      } else {
        Common.SweetAlert.alert(response?.message || "Failed to save recurring holiday", "error");
      }
    } catch (error) {
      console.error("Error saving recurring holiday:", error);
      action.setSubmitting(false);
      Common.SweetAlert.alert("Failed to save recurring holiday", "error");
    }
  };


  const addRecurringTime = (formikProps) => {
    const currentTimes = formikProps.values.recurringTimes || [];
    formikProps.setFieldValue("recurringTimes", [
      ...currentTimes,
      { start_time: "", end_time: "", description: "" }
    ]);
  };

  const removeRecurringTime = (formikProps, index) => {
    const currentTimes = formikProps.values.recurringTimes || [];
    if (currentTimes.length > 1) {
      const newTimes = currentTimes.filter((_, i) => i !== index);
      formikProps.setFieldValue("recurringTimes", newTimes);
    }
  };

  const updateRecurringTime = (formikProps, index, field, value) => {
    const currentTimes = [...(formikProps.values.recurringTimes || [])];
    currentTimes[index] = { ...currentTimes[index], [field]: value };
    formikProps.setFieldValue("recurringTimes", currentTimes);
  };

  const renderDaySelector = (formikProps) => {
    const handleDayToggle = (dayName) => {
      const currentDays = formikProps.values.daysOfWeek || [];
      if (currentDays.includes(dayName)) {
        // Remove day if already selected
        formikProps.setFieldValue("daysOfWeek", currentDays.filter(day => day !== dayName));
      } else {
        // Add day if not selected
        formikProps.setFieldValue("daysOfWeek", [...currentDays, dayName]);
      }
    };
    return (
      <div className="flex flex-wrap gap-2">
        {Common.Utility.enumToArray(DayOfWeekEnum).map((day) => {
          const isSelected = formikProps.values.daysOfWeek?.includes(day.name) || false;          
          return (
            <Button
              key={day.id}
              variant={isSelected ? "default" : "outline"}
              onClick={() => handleDayToggle(day.name)}
              className="w-24"
            >
              {day.name}
            </Button>
          );
        })}
      </div>
    );
  };

  const renderRecurringTimes = (formikProps) => {
    const { recurringTimes = [] } = formikProps.values;

        return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-medium text-gray-900 flex items-center gap-2">
            <Icon icon="heroicons:clock" className="h-5 w-5" />
            Recurring Times *
          </h4>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addRecurringTime(formikProps)}
            className="flex items-center gap-2"
          >
            <Icon icon="heroicons:plus" className="h-4 w-4" />
            Add Time Slot
          </Button>
        </div>

        {recurringTimes.map((time, index) => (
          <div key={index} className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">
                Time Slot {index + 1}
              </span>
              {recurringTimes.length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeRecurringTime(formikProps, index)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Icon icon="heroicons:trash" className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time *
                </label>
                <input
                  type="time"
                  value={time.start_time}
                  onChange={(e) => updateRecurringTime(formikProps, index, "start_time", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time *
                </label>
                  <input
                  type="time"
                  value={time.end_time}
                  onChange={(e) => updateRecurringTime(formikProps, index, "end_time", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={time.description}
                  onChange={(e) => updateRecurringTime(formikProps, index, "description", e.target.value)}
                  placeholder="e.g., Namaz Break, Prayer Time"
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        ))}
          </div>
        );
  };

  // Determine if we're in edit mode
  const isEditMode = !!clubHolidayRecurringId;

  return (
    <Formik
      initialValues={formInitialValue}
      validationSchema={getFormValidations(isEditMode)}
      innerRef={formikRef}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={onSubmitData}
    >
      {(formikProps) => {
        formikRef.current = formikProps;
        return (
          <>
            {isLoading && (
              <div className="flex items-center justify-center p-8">
                <div className="flex items-center gap-2 text-blue-600">
                  <Icon icon="heroicons:arrow-path" className="h-5 w-5 animate-spin" />
                  <span>Loading holiday details...</span>
                </div>
              </div>
            )}
            <div className="space-y-6">
              <Card title={`${clubHolidayRecurringId ? "Edit" : "Add"} Recurring Holiday`}>
                <div className="space-y-6 p-6">
                  {/* Basic Information */}
                  
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                    <div className="flex flex-col gap-3">
                      <FormikInput
                        formikProps={formikProps}
                        name={"startDate"}
                        placeholder={""}
                        label={"From Date"}
                        type={"datetime-local"}
                        min={isEditMode ? undefined : Common.Utility.todayDatetime}
                        disabled={isEditMode}
                      />
                    </div>

                    <div className="flex flex-col gap-3">
                      <FormikInput
                        formikProps={formikProps}
                        name={"endDate"}
                        placeholder={""}
                        label={"Till Date"}
                        type={"datetime-local"}
                        min={
                          formikProps.values.startDate ||
                          Common.Utility.todayDatetime
                        }
                      />
                    </div>
                  </div>

                  {/* add check box for is specific court */}
                  <div className="space-x-1 mb-4">
                        <AppCheckBox
                          checked={isSpecificCourt}
                          onChange={(checked) => {
                            setIsSpecificCourt(checked);
                            // Clear courtIds and sport selection when unchecked
                            if (!checked) {
                              formikProps.setFieldValue("courtIds", []);
                              formikProps.setFieldValue("sportIds", []);
                              setSelectedSports([]);
                              setAvailableCourts([]);
                            }
                          }}
                          text={<span className="font-bold text-md">For Specific Court</span>}   
                        />
                  </div>

                   {isSpecificCourt && (
                       <div className="flex flex-col gap-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-3">
                              <FormikSearchSelect
                                formikProps={formikProps}
                                name={"sportIds"}
                                data={sports}
                                placeholder={"Select Sports"}
                                label={"Select Sports *"}
                                isMulti={true}
                                className="w-full"
                                labelKey="label"
                                valueKey="value"
                                onChange={(sportIds) => {
                                  setSelectedSports(sportIds || []);
                                  formikProps.setFieldValue("sportIds", sportIds || []);
                                  // Update available courts when sports change
                                  getCombinedCourts(sportIds || []);
                                  // Clear court selection when sports change
                                  formikProps.setFieldValue("courtIds", []);
                                }}
                              />
                            </div>
                            <div className="flex flex-col gap-3">
                              <FormikSearchSelect
                                formikProps={formikProps}
                                name={"courtIds"}
                                data={availableCourts}
                                placeholder={"Select Courts"}
                                label={"Select Courts *"}
                                isMulti={true}
                                disabled={!formikProps.values.sportIds || formikProps.values.sportIds.length === 0}
                                className="w-full"
                                labelKey="displayText"
                                valueKey="value"
                              />
                            </div>
                          </div>
                       </div>
                   )}

                  <div className="flex flex-col gap-2">
                    <FormikTextArea
                      formikProps={formikProps}
                      name={"reason"}
                      placeholder={"Type..."}
                      type={"textarea"}
                      label={"Holiday Reason"}
                      maxLength={200}
                    />
                  </div>
                  
                  {/* Days Selection */}
                    <div className="border-t pt-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                        <Icon icon="heroicons:calendar" className="h-5 w-5" />
                      Select Days
                      </h3>
                      {renderDaySelector(formikProps)}
                  </div>

                  {/* Recurring Times */}
                  <div className="border-t pt-4">
                    {renderRecurringTimes(formikProps)}
                  </div>

                </div>
              </Card>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                disabled={formikProps.isSubmitting}
                onClick={(e) => {
                  e.preventDefault();
                  formikProps.handleSubmit();
                }}
                type="button"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {formikProps.isSubmitting ? "Saving..." : (clubHolidayRecurringId ? "Update Recurring Holiday" : "Save Recurring Holiday")}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/app/clubholidayrecurrings")}
              >
                Cancel
              </Button>
            </div>
          </>
        );
      }}
    </Formik>
  );
}